import debounce from "lodash/debounce"
import isBoolean from "lodash/isBoolean"
import isFunction from "lodash/isFunction"
import isNil from "lodash/isNil"
import isNumber from "lodash/isNumber"
import max from "lodash/max"
import maxBy from "lodash/maxBy"
import toUpper from "lodash/toUpper"

import addDays from "date-fns/addDays"
import endOfDay from "date-fns/endOfDay"
import formatDistanceStrict from "date-fns/formatDistanceStrict"
import isSameDay from "date-fns/isSameDay"
import isValid from "date-fns/isValid"
import parseISO from "date-fns/parseISO"
import startOfDay from "date-fns/startOfDay"

import Bowser from "bowser"
import tinycolor from "tinycolor2"

import {
    EXTENSION_GROUPS,
    NATIVELY_PREVIEWABLE_EXTENSIONS,
    PREVIEWABLE_EXTENSIONS,
    PROGRAM_MODALITIES,
    RESOURCE_TYPES,
} from "~/lib/constants"

import { dateFnsLocales } from "~/i18n"
import {
    animate,
    clamp,
    downloadBlob,
    fmtDigitalTimeHoursMinsSecs,
    fmtDigitalTimeMinsSecs,
    focusFirstFocusable,
    focusLastFocusable,
    html2text,
    imageWithVeil,
    isFocusable,
    linearRegression,
    secsToHoursMinsSecs,
    ucfirst,
} from "front-toolkit/lib/utils"

/**
 * Linear interpolation between a and b with parameter t between 0 and 1
 */
export function lerp(a, b, t) {
    return a + t * (b - a)
}

export function sum(xs) {
    return xs.reduce((acc, cur) => acc + cur, 0)
}

export function arrayToObject(array, key = "prop") {
    return array.reduce((obj, item) => {
        return {
            ...obj,
            [item[key]]: item,
        }
    }, {})
}

/**
 * zip(["a", "b", "c"], [1, 2, 3]) => [["a", 1], ["b", 2], ["c", 3]]
 */
export function zip() {
    const args = Array.from(arguments)
    const maxLength = max(
        args.map(function (a) {
            return a.length
        })
    )
    const out = []
    for (let i = 0; i < maxLength; i++) {
        out.push(
            args.map(function (arg) {
                return arg[i]
            })
        )
    }
    return out
}

export function capitalize(s) {
    return ucfirst(new String(s).toLowerCase())
}

/**
 * Uppercase the whole string
 */
export function ucall(s) {
    return toUpper(s)
}

/**
 * It counts occurrences of a pattern in a string
 * @param {str} haystack
 * @param {regex} needle
 * @return {int}
 */
export function countPattern(needle, haystack) {
    if (!needle) {
        throw new Error("countPattern: undefined needle")
    }
    if (!haystack) {
        throw new Error("countPattern: undefined haystack")
    }
    return haystack.length - haystack.replace(needle, "").length
}

/**
 * It creates a function that checks if the number of occurrences of
 * a pattern lies in the given interval.
 * @see countPattern
 * @param {regex} needle - pattern to count
 * @param {number} min - Interval min (inclusive)
 * @param {number} max - Interval max (inclusive)
 * @return {function(haystack): int} - A function that checks if occurrences lie between boundaries
 */

export const occurrencesBetween = (needle, min, max) => {
    if (!needle) {
        throw new Error("occurrencesBetween: undefined needle")
    }
    if (!min) min = 0
    if (!max) max = Infinity
    if (typeof min !== "number" || typeof max !== "number" || min > max) {
        throw new Error(
            `occurrencesBetween: wrong interval:\nmin: ${min}\nmax: ${max}`
        )
    }

    return value => {
        if (!value) {
            return false
        }
        const occurrences = countPattern(needle, value)
        return min <= occurrences && occurrences <= max
    }
}

/**
 * Very short date: DD/MM/YY
 */
export function fmtveryshortdate(locale, timeZone, date) {
    return date.toLocaleString(locale, {
        timeZone,
        day: "numeric",
        month: "numeric",
        year: "2-digit",
    })
}

/**
 * Short date: DD/MM/YYYY
 */
export function fmtdateshort(locale, timeZone, date) {
    return date.toLocaleString(locale, {
        timeZone,
        day: "numeric",
        month: "numeric",
        year: "numeric",
    })
}

/**
 * Date
 */
export function fmtdate(locale, timeZone, date) {
    return date.toLocaleString(locale, {
        timeZone,
        day: "numeric",
        month: "short",
        year: "numeric",
    })
}

/**
 * Long date
 */
export function fmtdatelong(locale, timeZone, date) {
    return date.toLocaleString(locale, {
        timeZone,
        day: "numeric",
        month: "long",
        year: "numeric",
    })
}

/**
 * Time
 */
export function fmttime(locale, timeZone, date) {
    return date.toLocaleTimeString(locale, {
        timeZone,
        hour: "numeric",
        minute: "numeric",
    })
}

/**
 * Date + Time
 */
export function fmtdatetime(locale, timeZone, date) {
    return date.toLocaleString(locale, { timeZone })
}

/**
 * Day of the week
 */
export function fmtweekday(locale, timeZone, date) {
    return date.toLocaleString(locale, { timeZone, weekday: "long" })
}

/**
 * Day of the week (short)
 */
export function fmtweekdayshort(locale, timeZone, date) {
    return date.toLocaleString(locale, { timeZone, weekday: "short" })
}

/**
 * Day of the month
 */
export function fmtmonthday(locale, timeZone, date) {
    return date.toLocaleString(locale, { timeZone, day: "numeric" })
}

/**
 * Month name
 */
export function fmtmonth(locale, timeZone, date) {
    return date.toLocaleString(locale, { timeZone, month: "long" })
}

/**
 * Month name, using short format
 */
export function fmtmonthshort(locale, timeZone, date) {
    return date.toLocaleString(locale, { timeZone, month: "short" })
}

/**
 * Month name, using short format + year using 4 digits
 */
export function fmtmonthyear(locale, timeZone, date) {
    return date.toLocaleString(locale, {
        timeZone,
        month: "short",
        year: "numeric",
    })
}

export function getFnsLocale(locale) {
    return dateFnsLocales[locale]
}

/**
 * Return the distance between the given date and now in words.
 */
export function fmtwordsago(locale, date, refDate = new Date()) {
    return formatDistanceStrict(date, refDate, {
        locale: getFnsLocale(locale),
    })
}

/**
 * Returns a human-readable representation of a size given in bytes
 */
export function fmtsize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    const exp = Math.floor(Math.log(bytes) / Math.log(1024))
    const pre = "KMGTPE".charAt(exp - 1)
    return `${(bytes / Math.pow(1024, exp)).toFixed(2)} ${pre}B`
}

export function fmtbignumber(number) {
    const sign = number < 0 ? "-" : ""
    let symbol = ""
    number = Math.abs(number)
    if (number < 10000) {
        // do nothing
    } else if (number < 999.95e3) {
        number = `${(number / 1e3).toFixed(1)}`
        symbol = "K"
    } else {
        number = `${(number / 1e6).toFixed(1)}`
        symbol = "M"
    }
    // Chop decimal if it's zero.
    if (number[number.length - 1] === "0") {
        number = number.slice(0, -2)
    }
    return `${sign}${number}${symbol}`
}

/**
 * Formats a number of seconds into a "(0)hours:(0)minutes" fashion
 * @param secs Number - a number of seconds
 * @returns String - using (0)hours:(0)minutes format
 */
export function fmtDigitalTimeHoursMins(seconds) {
    if (seconds > 0) {
        const time = secsToHoursMinsSecs(seconds)

        const hours = time.hours > 9 ? time.hours : "0" + time.hours
        const mins = time.mins > 9 ? time.mins : "0" + time.mins

        return `${hours}:${mins}`
    }
    return "00:00"
}

/**
 * Formats a number of seconds into a "${hours}h ${minutes}m" fashion.
 * If input is 0, returns "0s".
 * If input is 60, returns "1m".
 * If input is 3601, returns "1h" (discards seconds if there are hours).
 * @param {number} sec - a number of seconds
 * @returns {string} - formatted as '${hours}h ${minutes}m' or similar
 */
export function fmtTimeHourMin(sec) {
    const { hours, mins, secs } = secsToHoursMinsSecs(sec)
    if (hours) {
        return mins ? `${hours}h ${mins}m` : `${hours}h`
    }
    if (mins) {
        return `${mins}m`
    }
    return `${secs || 0}s`
}

/**
 * Ensure string is at most `len` characters long
 */
export function ellipsis(str, len) {
    if (str.length <= len) return str
    return str.slice(0, len - 1) + "…"
}

export function coalesce(string) {
    return string ? string.trim().replace(/\s\s+/g, " ") : ""
}

export function cleanHtml(html) {
    return coalesce(html2text(html))
}

/**
 * Depth-first Preorder traverse a tree-like structure, calling `cb`
 * on each node. Uses the property name given to find
 * a node's children (Default: "children").
 *
 * If the callback function returns a truthy value,
 * traversal is stopped.
 *
 * depth serves you to know how deep in the tree you are during the traverse
 */
export function traverse(
    node,
    cb,
    prop = "children",
    _depth = 0,
    _parent = null
) {
    if (cb(node, _depth, _parent)) {
        return true
    }
    const children = isFunction(prop) ? prop(node) : Array.from(node[prop])
    if (children.length) {
        for (const child of children) {
            if (traverse(child, cb, prop, _depth + 1, node)) {
                return true
            }
        }
    }
}

export function traverseBackwards(
    node,
    cb,
    prop = "children",
    _depth = 0,
    _parent = null
) {
    if (cb(node, _depth, _parent)) {
        return true
    }
    const children = isFunction(prop) ? prop(node) : Array.from(node[prop])
    if (children.length) {
        for (let i = children.length - 1; i >= 0; i--) {
            const child = children[i]
            if (traverseBackwards(child, cb, prop, _depth + 1, node)) {
                return true
            }
        }
    }
}

/**
 * Returns a promise that resolves when `cb` returns true.
 * Rejects if `timeout` ms are reached.
 *
 * Warning: Every time you use waitFor, you're trying to do something that neither
 * JS nor Vue are designed for: blocking calls.
 * With great power comes great responsability.
 */
export function waitFor(cb, timeout) {
    const start = performance.now()
    return new Promise((resolve, reject) => {
        const id = setInterval(() => {
            const now = performance.now()
            if (now - start > timeout) {
                clearInterval(id)
                reject(new Error(`Waiting failed after ${timeout}ms.`))
            } else {
                try {
                    const val = cb()
                    if (val) {
                        clearInterval(id)
                        resolve(val)
                    }
                } catch (_) {
                    // It's fine
                }
            }
        })
    })
}

/**
 * Returns a promise that resolves after `ms` ms.
 */
export function sleep(ms) {
    return new Promise(resolve => {
        setTimeout(resolve, ms)
    })
}

/**
 * Returns true if the given coordinates are inside the given element.
 * Ignores transformations for `el`, but not for its ancestors.
 * `x` ^ `y` may be null, and if so, that axis is not checked.
 *
 * @deprecated this is crap
 */
export function inside(el, x = null, y = null) {
    if (el.nodeType == Node.COMMENT_NODE) return false

    let left = el.offsetLeft
    let top = el.offsetTop

    if (el.offsetParent) {
        const rect = el.offsetParent.getBoundingClientRect()
        left += rect.left
        top += rect.top
    }

    const rect = el.getBoundingClientRect()

    const right = left + rect.width
    const bottom = top + rect.height

    let inside = true
    if (x != null) inside &= x >= left && x < right
    if (y != null) inside &= y >= top && y < bottom
    return inside
}

/**
 * Generate an UUIDv4.
 * From the glorious SO answer: https://stackoverflow.com/a/2117523
 */
export function uuid() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
        let r = (Math.random() * 16) | 0
        let v = c == "x" ? r : (r & 0x3) | 0x8
        return v.toString(16)
    })
}

/**
 * I can't never remember the name of this method, so fuck it.
 * rherrero: 30/08/2018
 */
export function rect(el) {
    return el.getBoundingClientRect()
}

/**
 * Call `cb` `n` times, with the iteration index.
 */
export function times(n, cb) {
    const vs = []
    for (let i = 0; i < n; i++) {
        vs.push(cb(i))
    }
    return vs
}

/**
 * Returns a random Number inside [`min`, `max`)
 */
export function rand(min, max) {
    return Math.random() * (max - min) + min
}

/**
 * Returns a random *integer* Number inside [`min`, `max`)
 */
export function randi(min, max) {
    min = Math.ceil(min)
    max = Math.floor(max)
    return Math.floor(Math.random() * (max - min)) + min
}

export async function downloadResponseFile({ response, fileName, t }) {
    if (!response.ok) {
        throw new Error(t("errors.fileGenerationError"))
    }
    const blob = await response.blob()
    downloadBlob({ blob, fileName })
}

/**
 * Open a file request window. Resolves to an Array of
 * selected Files.
 */
export function requestFiles({
    accept = "",
    multiple = false,
    doc = document,
} = {}) {
    return new Promise(resolve => {
        const input = doc.createElement("input")

        input.setAttribute("type", "file")
        if (multiple) input.setAttribute("multiple", true)
        if (accept) input.setAttribute("accept", accept)
        input.style.opacity = "0"

        input.addEventListener("change", () => {
            const files = Array.from(input.files || [])
            resolve(files)
            doc.body.removeChild(input)
        })

        doc.body.appendChild(input)
        input.click()
    })
}

/**
 * File -> "data:[<mediatype>][;base64],<data>"
 */
export function readAsDataURL(file) {
    return new Promise(resolve => {
        const reader = new window.FileReader()
        reader.addEventListener("load", () => {
            resolve(reader.result)
        })
        reader.readAsDataURL(file)
    })
}

export function isFullScreenSupported(el) {
    if (
        el.requestFullscreen ||
        el.webkitRequestFullscreen ||
        el.mozRequestFullScreen ||
        el.msRequestFullscreen
    ) {
        return true
    } else {
        return false
    }
}

/**
 * Requests the browser to exit the full screen mode
 */
export function exitFullscreen() {
    if (document.exitFullscreen) {
        return document.exitFullscreen()
    } else if (document.webkitExitFullscreen) {
        return document.webkitExitFullscreen()
    } else if (document.mozCancelFullScreen) {
        return document.mozCancelFullScreen()
    } else if (document.msExitFullscreen) {
        return document.msExitFullscreen()
    } else {
        throw new Error("Fullscreen API is not supported.")
    }
}

/**
 * Requests the browser to start the full screen mode
 */
export function requestFullscreen(el) {
    if (el.requestFullscreen) {
        return el.requestFullscreen()
    } else if (el.webkitRequestFullscreen) {
        return el.webkitRequestFullscreen()
    } else if (el.mozRequestFullScreen) {
        return el.mozRequestFullScreen()
    } else if (el.msRequestFullscreen) {
        return el.msRequestFullscreen()
    } else {
        throw new Error("Full-screen API is not supported.")
    }
}

/**
 * Transforms a number into a letter:
 * 0 -> A
 * 1 -> B
 * 2 -> C
 * ...
 * 25 -> Z
 */
export function numberToLetter(number) {
    if (number < 0 || number > 25) {
        throw new Error(
            `This function is limited to numbers between 0 & 25, the value used was ${number}`
        )
    }
    return String.fromCharCode(65 + number)
}

export function isPreviewable(extension) {
    extension = extension.toLowerCase()
    return PREVIEWABLE_EXTENSIONS.includes(extension)
}

export function isNativelyPreviewable(extension) {
    extension = extension.toLowerCase()
    return NATIVELY_PREVIEWABLE_EXTENSIONS.includes(extension)
}

/**
 * "foo=bar&baz=cat&order=name" => { foo: "bar", baz: "cat", "orderBy": "name" }
 * Without the initial: `?`
 */
export function parseQueryString(queryString = "") {
    const fields = queryString.split("&")
    const values = {}
    for (const field of fields) {
        if (!field) continue
        const [name, value] = field.split("=")
        values[name] = decodeURIComponent(value)
    }
    return values
}

export function tryParseValue(str) {
    {
        // Boolean
        if (["true", "false"].includes(str)) {
            return str === "true"
        }
    }

    {
        // Number
        const number = Number(str)
        if (!Number.isNaN(number)) {
            return number
        }
    }

    return str
}

/**
 * { foo: "bar", baz: "cat" } => "foo=bar&baz=cat"
 * Without the initial: `?`
 */
export function buildQueryString(fields) {
    return Object.entries(fields)
        .filter(([_, value]) => !isNil(value) && value !== "")
        .map(([name, value]) => `${name}=${encodeURIComponent(value)}`)
        .join("&")
}

export function filterHasActiveValue(value, description) {
    if (isNil(value)) return false

    if (description) {
        switch (description.type) {
            case "text":
                return value !== ""

            case "number":
                return true

            case "switch":
            case "checkbox":
                return value

            case "select":
            case "select-remote":
                return value !== null

            case "checkbox-group":
            case "multi-select":
            case "multi-select-remote":
                return value.length > 0

            case "rating":
                return value > 0

            case "size":
            case "range": {
                const { min = 0, max = Infinity } = description
                const { start, end } = value
                return start !== min || end !== max
            }

            case "date":
                return "start" in value || "end" in value

            default:
                throw new Error(`Unknown filter type ${description.type}`)
        }
    } else {
        return value !== ""
    }
}

export function numFiltersWithActiveValue(filterValues, filterDescriptions) {
    return Object.entries(filterValues).reduce((acc, [name, value]) => {
        const desc = filterDescriptions.find(f => f.prop === name)
        return filterHasActiveValue(value, desc) ? acc + 1 : acc
    }, 0)
}

export function escapeChars(str, reservedCharacters, escapeChar = "\\") {
    str = str.toString()
    let out = ""
    for (let i = 0; i < str.length; i++) {
        const char = str[i]
        if (reservedCharacters.concat(escapeChar).includes(char)) {
            out += `${escapeChar}${char}`
        } else {
            out += char
        }
    }
    return out
}

export function unescapeChars(str, escapeChar = "\\") {
    str = str.toString()
    let out = ""
    for (let i = 0; i < str.length; i++) {
        const char = str[i]
        if (char === escapeChar) continue
        out += char
    }
    return out
}

export function buildQueryFilters(
    values = {},
    descriptions = [],
    operators = {}
) {
    const reservedCharacters = [":", ";", "|", "[", "]"]
    const escape = v => escapeChars(v, reservedCharacters)
    const pairs = []
    for (const [name, value] of Object.entries(values)) {
        const description = descriptions.find(f => f.prop === name)

        if (!filterHasActiveValue(value, description)) continue

        if (description) {
            switch (description.type) {
                case "text": {
                    pairs.push(`${name}:${escape(value)};LIKE`)
                    break
                }

                case "number": {
                    pairs.push(`${name}:${value}`)
                    break
                }

                case "switch":
                case "checkbox": {
                    pairs.push(`${name}:${value}`)
                    break
                }

                case "select":
                case "select-remote":
                    pairs.push(`${name}:${escape(value)}`)
                    break

                case "checkbox-group":
                case "multi-select":
                case "multi-select-remote":
                    for (const item of value) {
                        pairs.push(`${name}[]:${escape(item)}`)
                    }
                    break

                case "rating": {
                    pairs.push(`${name}:${value}`)
                    break
                }

                case "size":
                case "range": {
                    const { min = 0, max = Infinity } = description
                    const { start, end } = value
                    if (start > min) {
                        pairs.push(`${name}:${start};GE`)
                    }
                    if (end < max) {
                        pairs.push(`${name}:${end};LE`)
                    }
                    break
                }

                case "date": {
                    let { start, end } = value
                    if (start) {
                        pairs.push(`${name}:${escape(start)};GE`)
                    }
                    if (end) {
                        // We add one day to the end date so it includes the end date in the results
                        end = addDays(parseISO(end), 1).toISOString()
                        pairs.push(`${name}:${escape(end)};LE`)
                    }
                    break
                }

                default:
                    throw new Error(`Unknown filter type ${description.type}`)
            }
        } else {
            if (Array.isArray(value)) {
                value.forEach((item, index) => {
                    let pair = `${name}[]:${escape(item)}`
                    if (operators[name]?.[index])
                        pair += `;${operators[name][index]}`
                    pairs.push(pair)
                })
            } else if (typeof value === "object") {
                // It has to be a date or number range
                let { start, end } = value
                if (start) {
                    pairs.push(`${name}:${escape(start)};GE`)
                }
                if (end) {
                    if (isValid(parseISO(end))) {
                        // We add one day to the end date so it includes the end date in the results
                        end = addDays(parseISO(end), 1).toISOString()
                    }
                    pairs.push(`${name}:${escape(end)};LE`)
                }
            } else {
                let pair = `${name}:${escape(value)}`
                if (operators[name]) pair += `;${operators[name]}`
                pairs.push(pair)
            }
        }
    }

    return pairs.join("|")
}

function _reachYiqThresold(color) {
    const { r, g, b } = tinycolor(color).toRgb()
    const yiq = (r * 299 + g * 587 + b * 114) / 1000
    return yiq >= 160
}

// Based on the color-yiq bootstrap function.
export function getContrastColor(color) {
    if (_reachYiqThresold(color)) {
        return "#606060"
    } else {
        return "#ffffff"
    }
}

export function getContrastTheme(color) {
    if (_reachYiqThresold(color)) {
        return "dark"
    } else {
        return "light"
    }
}

export function lighten(color, amount) {
    color = tinycolor(color)
    return tinycolor.mix(color, "#fff", amount).toHexString()
}

export function darken(color, amount) {
    color = tinycolor(color)
    return tinycolor.mix(color, "#000", amount).toHexString()
}

// Darken or lighten color based on same criteria than getContrastColor
export function getOverlayColor(color, amount) {
    const transform = _reachYiqThresold(color) ? darken : lighten
    return transform(color, amount)
}

export function guessPlayerFromExtension(extension) {
    extension = extension.toLowerCase()
    if (EXTENSION_GROUPS.image.includes(extension)) {
        return "image"
    } else if (EXTENSION_GROUPS.video.includes(extension)) {
        return "video"
    } else if (EXTENSION_GROUPS.audio.includes(extension)) {
        return "audio"
    } else if (EXTENSION_GROUPS.text.includes(extension)) {
        return "text"
    } else if (EXTENSION_GROUPS.doc.includes(extension)) {
        return "pdf"
    } else {
        throw new Error(`Type ${extension} has no player defined`)
    }
}

export function getExtensionIcon(extension) {
    if (EXTENSION_GROUPS.image.includes(extension)) {
        return "img-doc"
    }

    if (EXTENSION_GROUPS.video.includes(extension)) {
        return "video-doc"
    }

    if (EXTENSION_GROUPS.audio.includes(extension)) {
        return "audio-doc"
    }

    if (EXTENSION_GROUPS.text.includes(extension)) {
        return "file-alt"
    }

    if (EXTENSION_GROUPS.compressed.includes(extension)) {
        return "zip"
    }

    if (["pdf"].includes(extension)) {
        return "pdf"
    }

    if (["doc", "docx", "odt"].includes(extension)) {
        return "word"
    }

    if (["xls", "xlsx", "ods"].includes(extension)) {
        return "xls"
    }

    if (["ppt", "pptx", "odp"].includes(extension)) {
        return "ppt"
    } else {
        return "file-alt"
    }
}

/**
 * True if all needles are found in haystack
 */
export function includesAll(haystack, needles) {
    return needles.every(needle => haystack.includes(needle))
}

export function translateProgramModality(t, modality) {
    modality = modality.toLowerCase()
    if (!PROGRAM_MODALITIES.includes(modality)) {
        throw new Error(`Unknown program modality ${modality}`)
    }

    switch (modality) {
        case "online":
            return t("common.online")

        case "offline":
            return t("common.offline")

        case "blended":
        case "mixed":
            return t("common.blended")

        default:
            throw new Error(`Unknown program modality ${modality}`)
    }
}

export function translateResourceType(t, type, extension, mimeType) {
    if (!RESOURCE_TYPES.includes(type)) {
        throw new Error(`Unknown resource type ${type}`)
    }

    switch (type) {
        case "activity": {
            if (mimeType == "activity/scorm") {
                return t("common.resourceTypeFileActivity")
            } else if (mimeType == "activity/social_video") {
                return t("common.socialVideo")
            } else if (mimeType == "activity/scorm_video") {
                return t("common.scormVideo")
            } else {
                throw new Error(
                    `Don't know how to translate ${type} ${extension} ${mimeType}`
                )
            }
        }
        case "audio":
            return t("common.resourceTypeFileAudio")
        case "document": {
            switch (extension) {
                case "ppt":
                case "pptx":
                    return t("common.resourceTypeFilePresentation")

                case "xls":
                case "xlsx":
                    return t("common.resourceTypeFileCalcSheet")

                default:
                    return t("common.resourceTypeFileDocument")
            }
        }
        case "image":
            return t("common.resourceTypeFileImage")
        case "miscellany":
            return t("common.resourceTypeFileMiscellany")
        case "podcast":
            return t("common.resourceTypeFilePodcast")
        case "presentation":
            return t("common.resourceTypeFilePresentation")
        case "url":
            return t("common.resourceTypeFileUrl")
        case "video":
            return t("common.resourceTypeFileVideo")
        case "videocast":
            return t("common.resourceTypeFileVideocast")
        default:
            throw new Error("Shouldn't happen")
    }
}

// "14 de marzo de 2011 | 00:00 - 01:00"
// "14 de marzo de 2011 | 04:00 - 15 de marzo de 2011 | 01:00"
export function formatDateRange(startDate, endDate, locale, timezone) {
    startDate = parseISO(startDate)
    endDate = parseISO(endDate)

    if (isSameDay(startDate, endDate)) {
        return `${fmtdatelong(locale, timezone, startDate)} | ${fmttime(locale, timezone, startDate)} - ${fmttime(locale, timezone, endDate)}`
    } else {
        return `${fmtdatelong(locale, timezone, startDate)} | ${fmttime(locale, timezone, startDate)} - ${fmtdatelong(locale, timezone, endDate)} | ${fmttime(locale, timezone, endDate)}`
    }
}

/**
 * Takes an array of ItemDescription and returns an array in a format easier
 * to use from a Vue template.
 * Can include extra attributes for each item.
 */
export function cookItemsForCards(cardMap, items, extraAttrs = {}) {
    return items.map(({ type, value }) => {
        const key = value._meta.key
        const component = cardMap[type]
        if (!component) {
            throw new Error(`Card ${type} was not found it the card map`)
        }
        return {
            key: `${type}:${value[key]}`,
            component,
            value,
            attrs: {
                item: value,
                ...extraAttrs,
            },
        }
    })
}

/**
 * Groups items by the value returned by the callback, but only if they are contiguous.
 * [Type] => [[Type]]
 */
export function groupContiguousBy(xs, cb) {
    const xxs = []

    let lastValue = null
    for (let x of xs) {
        if (xxs.length == 0) {
            lastValue = cb(x)
            xxs.push([x])
            continue
        }

        const value = cb(x)
        if (value == lastValue) {
            xxs.at(-1).push(x)
        } else {
            xxs.push([x])
        }
        lastValue = value
    }

    return xxs
}

/**
 * Tests position: sticky availability.
 */
export function doesPositionStickyWork() {
    const link = document.createElement("a")
    link.style.cssText = "position:sticky;"
    return link.style.position.indexOf("sticky") !== -1
}

/**
 * Add leading zeros to 'num' until it reachs 'size' digits
 */
export function pad(num, size) {
    let s = num + ""
    while (s.length < size) s = "0" + s
    return s
}

/**
 * Returns true if the passed timeZone is supported by the system
 * From https://stackoverflow.com/questions/44115681/javascript-check-if-timezone-name-valid-or-not
 */
export function supportedTimeZone(timeZone) {
    try {
        Intl.DateTimeFormat(undefined, { timeZone })
        return true
    } catch (_) {
        return false
    }
}

export function buildValidatorsForPasswordValidationRules(rules) {
    const validations = {}

    for (let rule of rules) {
        switch (rule.key) {
            case "lowercase":
                validations.lower = occurrencesBetween(
                    /[a-z]/g,
                    rule.min,
                    rule.max
                )
                break

            case "uppercase":
                validations.upper = occurrencesBetween(
                    /[A-Z]/g,
                    rule.min,
                    rule.max
                )
                break

            case "numeric":
                validations.numeric = occurrencesBetween(
                    /[0-9]/g,
                    rule.min,
                    rule.max
                )
                break

            case "symbol":
                validations.symbolic = occurrencesBetween(
                    // eslint-disable-next-line no-useless-escape
                    /[ºª\\!|"@·#$~%&/()=?¿'¡\[\]{}+,.;:-_<>]/g,
                    rule.min,
                    rule.max
                )
                break

            default:
                throw new Error(`Unknown validation rule: ${rule.key}`)
        }
    }

    return validations
}

/**
 * Returns an object containing the detected browser and operative system (os) used
 * @param {String} userAgent
 */
export function detectPlatform(userAgent = window.navigator.userAgent) {
    const browser = Bowser.getParser(userAgent)
    const operativeSystem = browser.getOSName()
    let browserName = browser.getBrowserName().toLowerCase()
    switch (browserName) {
        case "firefox":
        case "chrome":
        case "opera":
        case "safari":
            break
        case "chromium":
        case "samsung internet for android":
            browserName = "chrome"
            break
        case "microsoft edge":
            browserName = "edge"
            break
        case "internet explorer": {
            const browserVersion = browser.getBrowserVersion()
            browserName = `ie${Number(browserVersion)}`
            break
        }
        default:
            break
        // Do nothing, returns browserName as browser
    }

    return {
        os: operativeSystem,
        browser: browserName,
    }
}

/**
 * Debounces calls to `func` for which `keyCallback` returns the same key.
 * `wait` and `options` are passed "as is" to lodash/debounce.
 */
export function debounceBy(func, wait, options, keyCallback) {
    const repository = {}

    const fn = (...params) => {
        const key = keyCallback(...params)
        if (!(key in repository)) {
            repository[key] = debounce(func, wait, options)
        }
        return repository[key](...params)
    }

    // TODO this could be fancier, only canceling / flushing functions with a given key,
    // but it's good enough for now.
    fn.cancel = () => Object.values(repository).forEach(fn => fn.cancel())
    fn.flush = () => Object.values(repository).forEach(fn => fn.flush())

    return fn
}

/**
 * Returns an unfulfilled Promise and its resolve and reject functions.
 */
function newPromiseInPieces() {
    const output = {}
    output.promise = new Promise((resolve, reject) => {
        output.resolve = resolve
        output.reject = reject
    })
    return output
}

/**
 * Properly debounces an async function:
 * When the debounced function is called, it returns a Promise that resolves (or rejects)
 * when the original functions if finally executed.
 * Until the function is finally executed, all calls return the same Promise.
 * View tests for more details.
 */
export function debounceAsync(fn, ms) {
    let promisePieces = null
    const wrapper = (resolve, reject, ...args) => {
        fn(...args)
            .then(resolve, reject)
            .finally(() => {
                promisePieces = null
            })
    }
    const debouncedWrapper = debounce(wrapper, ms)

    return (...args) => {
        if (!promisePieces) promisePieces = newPromiseInPieces()
        const { promise, resolve, reject } = promisePieces
        debouncedWrapper(resolve, reject, ...args)
        return promise
    }
}

export function showBookmarked(social) {
    return social && isBoolean(social.bookmarked)
}

export function showLikes(social) {
    return social && isBoolean(social.liked)
}

export function showRating(social) {
    return social && (isNumber(social.rating) || isBoolean(social.rated))
}

export function showComments(social) {
    return social && isNumber(social.commentsCount)
}

export function formatTime(value, unit) {
    const codes = ["h", "m", "s"]
    const measures = {
        s: 1,
        m: 60,
        h: 60 * 60,
    }
    let seconds = value * measures[unit]
    let res = []
    for (let code of codes) {
        if (seconds >= measures[code]) {
            let number = Math.floor(seconds / measures[code])
            res.push(`${number}${code}`)
            seconds = seconds - number * measures[code]
        }
    }
    if (!res.length) res.push(`0${unit}`)
    return res.join(" ")
}

export function openSSO({ url, callback, interval = 500, popup }) {
    if (popup.window) {
        popup.window.focus()
        return
    }

    popup.window = window.open(url)
    let timer = null
    timer = window.setInterval(function () {
        if (!popup.window.opener) {
            callback()
            popup.window = null
            window.clearInterval(timer)
        }
    }, interval)
}

export function getUserInfo({ jobTitle, mainDomain, mainUnit }) {
    return [jobTitle, mainDomain, mainUnit].filter(v => !!v).join(" | ")
}

/**
 * A function that returns if a given date can be considered as midnight, taking into account a
 * threshold before and after 00:00 given in seconds
 * @param {Date} date
 * @param {number} threshold - Seconds
 * @return {boolean}
 */
export function isMidnight(date, threshold = 300) {
    threshold = threshold * 1000 // Seconds to milliseconds
    const previousMidnight = startOfDay(date).getTime()
    const nextMidnight = endOfDay(date).getTime()

    const eventDate = date.getTime()

    return (
        Math.abs(previousMidnight - eventDate) <= threshold ||
        Math.abs(nextMidnight - eventDate) <= threshold
    )
}

const DEFAULT_CANDIDATES = ["left", "right", "top", "bottom"]

export function getBiggestAvailableSpace(
    rect,
    viewport,
    candidates = DEFAULT_CANDIDATES
) {
    const { top, left, width, height } = rect

    const { width: vw, height: vh } = viewport

    const right = left + width
    const bottom = top + height

    const positions = [
        { position: "topLeft", width: vw - left, height: top },
        { position: "top", width: vw, height: top },
        { position: "topRight", width: right, height: top },

        { position: "bottomLeft", width: vw - left, height: vh - bottom },
        { position: "bottom", width: vw, height: vh - bottom },
        { position: "bottomRight", width: right, height: vh - bottom },

        { position: "leftTop", width: left, height: vh - top },
        { position: "left", width: left, height: vh },
        { position: "leftBottom", width: left, height: bottom },

        { position: "rightTop", width: vw - right, height: vh - top },
        { position: "right", width: vw - right, height: vh },
        { position: "rightBottom", width: vw - right, height: bottom },
    ].filter(({ position }) => candidates.includes(position))

    return maxBy(positions, ({ width, height }) => width * height)
}

export function getDateFormat(locale = "es") {
    const formats = {
        br: "DD/MM/YYYY",
        ct: "DD/MM/YYYY",
        de: "DD.MM.YYYY",
        en: "MM/DD/YYYY",
        es: "DD/MM/YYYY",
        eu: "YYYY/MM/DD",
        fr: "DD/MM/YYYY",
        it: "DD/MM/YYYY",
        pt: "DD/MM/YYYY",
    }
    return formats[locale]
}

export {
    animate,
    clamp,
    downloadBlob,
    fmtDigitalTimeHoursMinsSecs,
    fmtDigitalTimeMinsSecs,
    focusFirstFocusable,
    focusLastFocusable,
    html2text,
    imageWithVeil,
    isFocusable,
    linearRegression,
    secsToHoursMinsSecs,
    ucfirst,
}
